import {
  type Cliente,
  type Coordenada,
  type Empresa,
  type Endereco,
  type EntregaDoEntregador,
  type Entregador,
  FORMAS_PAGAMENTO,
  type FormaPagamento,
  type LocalDaLoja,
  ORIGENS_DO_LOCAL,
  type OrigemDoLocal,
  type ParteDoPagamento,
  type Pedido,
  type PosicaoEntregador,
  type Produto,
  type Rastreio,
  type ResumoCliente,
  type StatusPedido,
} from '@entregas/core';
import type { Linha, LinhaView } from './cliente';
import type { Database } from './database.types';

// Linha do banco (snake_case, nulos do Postgres) → tipo do domínio (camelCase).
// Toda conversão passa por aqui: as telas nunca veem nome de coluna.

function coordenada(lat: number | null, lng: number | null): Coordenada | null {
  return lat != null && lng != null ? { lat, lng } : null;
}

/** Origem do ponto ('geocodificado' | 'porta'); outro texto vira null (marcado à mão). */
export function paraOrigemDoLocal(valor: string | null): OrigemDoLocal | null {
  return ORIGENS_DO_LOCAL.find((o) => o === valor) ?? null;
}

function localDaLoja(l: Linha<'empresas'>): LocalDaLoja | null {
  if (l.lat == null || l.lng == null || !l.local_marcado_em) return null;
  return { lat: l.lat, lng: l.lng, precisaoM: l.local_precisao_m, marcadoEm: l.local_marcado_em };
}

function ehForma(valor: unknown): valor is FormaPagamento {
  return typeof valor === 'string' && (FORMAS_PAGAMENTO as readonly string[]).includes(valor);
}

/** Partes do pagamento (jsonb) → domínio. Entrada estranha vira lista vazia, nunca exceção. */
export function paraPagamentos(json: unknown): ParteDoPagamento[] {
  if (!Array.isArray(json)) return [];
  return json.flatMap((p: unknown) => {
    const parte = p as { forma?: unknown; valor_centavos?: unknown; parcelas?: unknown };
    if (!ehForma(parte.forma) || typeof parte.valor_centavos !== 'number') return [];
    return [
      {
        forma: parte.forma,
        valorCentavos: parte.valor_centavos,
        parcelas: typeof parte.parcelas === 'number' ? parte.parcelas : null,
      },
    ];
  });
}

export function paraEmpresa(l: Linha<'empresas'>): Empresa {
  return {
    id: l.id,
    nome: l.nome,
    telefone: l.telefone,
    viradaDiaHora: l.virada_dia_hora,
    cidade: l.cidade,
    uf: l.uf,
    taxaEntregaPadraoCentavos: l.taxa_entrega_padrao_centavos,
    fuso: l.fuso,
    local: localDaLoja(l),
    prazoMinutos: l.prazo_minutos,
    entregaGratisAPartirCentavos: l.entrega_gratis_a_partir_centavos,
    logo: l.logo,
  };
}

export function paraProduto(l: Linha<'produtos'>): Produto {
  return {
    id: l.id,
    empresaId: l.empresa_id,
    nome: l.nome,
    apelidos: l.apelidos,
    precoCentavos: l.preco_centavos,
    ordem: l.ordem,
    ativo: l.ativo,
  };
}

export function paraCliente(l: Linha<'clientes'>): Cliente {
  return {
    id: l.id,
    empresaId: l.empresa_id,
    nome: l.nome,
    telefone: l.telefone,
    observacao: l.observacao,
    criadoEm: l.criado_em,
  };
}

/** A view devolve tudo como anulável (limitação do Postgres): os padrões ficam aqui. */
export function paraResumoCliente(l: LinhaView<'clientes_resumo'>): ResumoCliente {
  return {
    id: l.id ?? '',
    empresaId: l.empresa_id ?? '',
    nome: l.nome ?? '',
    telefone: l.telefone ?? '',
    observacao: l.observacao,
    criadoEm: l.criado_em ?? '',
    totalPedidos: l.total_pedidos ?? 0,
    pedidosEntregues: l.pedidos_entregues ?? 0,
    ultimoPedidoEm: l.ultimo_pedido_em,
    ticketMedioCentavos: l.ticket_medio_centavos ?? 0,
  };
}

export function paraEndereco(l: Linha<'enderecos'>): Endereco {
  return {
    id: l.id,
    clienteId: l.cliente_id,
    rotulo: l.rotulo,
    logradouro: l.logradouro,
    numero: l.numero,
    complemento: l.complemento,
    bairro: l.bairro,
    cidade: l.cidade,
    referencia: l.referencia,
    coordenada: coordenada(l.lat, l.lng),
    principal: l.principal,
  };
}

export function paraEntregador(l: Linha<'entregadores'>): Entregador {
  return {
    id: l.id,
    empresaId: l.empresa_id,
    nome: l.nome,
    telefone: l.telefone,
    ativo: l.ativo,
    codigo: l.codigo,
    pareado: l.user_id != null,
    desativadoEm: l.desativado_em,
    regra: {
      porEntregaCentavos: l.pagamento_por_entrega_centavos,
      diariaCentavos: l.pagamento_diaria_centavos,
      taxaInteira: l.pagamento_taxa_inteira,
      percentualBp: l.pagamento_percentual_bp,
      desde: l.pagamento_desde,
    },
  };
}

export function paraPedido(l: Linha<'pedidos'>): Pedido {
  return {
    id: l.id,
    empresaId: l.empresa_id,
    numero: l.numero,
    clienteId: l.cliente_id,
    enderecoId: l.endereco_id,
    enderecoTexto: l.endereco_texto,
    destino: coordenada(l.destino_lat, l.destino_lng),
    itens: l.itens,
    valorCentavos: l.valor_centavos,
    taxaEntregaCentavos: l.taxa_entrega_centavos,
    formaPagamento: l.forma_pagamento,
    pagamentos: paraPagamentos(l.pagamentos),
    trocoParaCentavos: l.troco_para_centavos,
    observacao: l.observacao,
    status: l.status,
    entregadorId: l.entregador_id,
    motivo: l.motivo,
    tokenRastreio: l.token_rastreio,
    criadoEm: l.criado_em,
    saiuEm: l.saiu_em,
    entregueEm: l.entregue_em,
    concluidoEm: l.concluido_em,
  };
}

type LinhaMinhasEntregas = Database['public']['Functions']['minhas_entregas']['Returns'][number];

export function paraEntregaDoEntregador(l: LinhaMinhasEntregas): EntregaDoEntregador {
  return {
    id: l.id,
    numero: l.numero,
    status: l.status,
    clienteNome: l.cliente_nome,
    clienteTelefone: l.cliente_telefone,
    enderecoTexto: l.endereco_texto,
    destino: coordenada(l.destino_lat, l.destino_lng),
    destinoOrigem: paraOrigemDoLocal(l.destino_origem),
    itens: l.itens,
    valorCentavos: l.valor_centavos,
    taxaEntregaCentavos: l.taxa_entrega_centavos,
    formaPagamento: l.forma_pagamento,
    pagamentos: paraPagamentos(l.pagamentos),
    trocoParaCentavos: l.troco_para_centavos,
    observacao: l.observacao,
    motivo: l.motivo,
    tokenRastreio: l.token_rastreio,
    entregadorId: l.entregador_id,
    criadoEm: l.criado_em,
    saiuEm: l.saiu_em,
    entregueEm: l.entregue_em,
    concluidoEm: l.concluido_em,
  };
}

export function paraPosicao(l: Linha<'posicoes_entregador'>): PosicaoEntregador {
  return {
    entregadorId: l.entregador_id,
    lat: l.lat,
    lng: l.lng,
    precisao: l.precisao,
    // A hora do GPS, não a da chegada ao servidor: um lote preso sem sinal não parece novo (como o link).
    atualizadoEm: l.registrado_em,
  };
}

/** Forma do JSON devolvido por rastreio_publico() (ver migration de funções). */
interface RastreioJson {
  /** Hora do servidor (desde a migration *_rastreio_com_hora). */
  agora?: string;
  /** tem_logo chega desde a migration *_logo_da_loja; antes dela, falta (= sem logo). */
  loja: { nome: string; telefone: string | null; tem_logo?: boolean };
  pedido: {
    numero: number;
    status: StatusPedido;
    criado_em: string;
    saiu_em: string | null;
    entregue_em: string | null;
    cliente_primeiro_nome: string;
    destino: Coordenada | null;
    /** Desde a migration *_avaliacao_do_cliente; antes dela, faltam. */
    itens?: string;
    total_centavos?: number;
    cliente_confirmou_em?: string | null;
    pode_avaliar?: boolean;
  };
  avaliacao?: { nota: number; marcas: string[]; comentario: string | null } | null;
  entregador: {
    primeiro_nome: string;
    posicao: (Coordenada & { atualizado_em: string }) | null;
  } | null;
}

export function paraRastreio(json: unknown): Rastreio | null {
  if (json == null || typeof json !== 'object') return null;
  const r = json as RastreioJson;
  return {
    agora: r.agora ?? null,
    loja: { nome: r.loja.nome, telefone: r.loja.telefone, temLogo: r.loja.tem_logo === true },
    pedido: {
      numero: r.pedido.numero,
      status: r.pedido.status,
      criadoEm: r.pedido.criado_em,
      saiuEm: r.pedido.saiu_em,
      entregueEm: r.pedido.entregue_em,
      clientePrimeiroNome: r.pedido.cliente_primeiro_nome,
      destino: r.pedido.destino,
      itens: r.pedido.itens ?? '',
      totalCentavos: r.pedido.total_centavos ?? 0,
      clienteConfirmouEm: r.pedido.cliente_confirmou_em ?? null,
      podeAvaliar: r.pedido.pode_avaliar === true,
    },
    avaliacao: r.avaliacao
      ? { nota: r.avaliacao.nota, marcas: r.avaliacao.marcas, comentario: r.avaliacao.comentario }
      : null,
    entregador: r.entregador
      ? {
          primeiroNome: r.entregador.primeiro_nome,
          posicao: r.entregador.posicao
            ? {
                lat: r.entregador.posicao.lat,
                lng: r.entregador.posicao.lng,
                atualizadoEm: r.entregador.posicao.atualizado_em,
              }
            : null,
        }
      : null,
  };
}
